/**
 * Shared starburst routing helpers. This file imports NOTHING (not even a relative sibling) so
 * it trivially satisfies the project_starburst isolation boundary guard, and is imported by BOTH
 * `dashboard-ui`'s `CMx.tsx` (route table + full-screen-layout
 * classification) and this folder's own tests, so the "does this pathname match a project_starburst
 * route" logic is defined exactly once.
 *
 * `isProjectStarburstPath` takes a bare pathname (e.g. `window.location.pathname`), never a full
 * href, and must classify a pathname exactly the way react-router v5 `matchPath(pathname, { path,
 * exact: true })` would against the three route patterns below:
 *  - case-INsensitive (RR5's default `sensitive: false` -- none of the three routes sets
 *    `sensitive`);
 *  - each `:param` matches exactly one non-slash path segment;
 *  - `exact` treats a SINGLE trailing slash as optional, but does NOT tolerate a doubled trailing
 *    slash or any other superstring/substring of a route.
 * A prior version of this helper
 * (`pathname.replace(/\/+$/, '').endsWith('/project_starburst')`) diverged from RR5 on all three
 * points: it was case-sensitive, it collapsed any number of trailing slashes before comparing
 * (so `.../project_starburst//` wrongly matched), and it matched ANY path merely ending in the
 * segment regardless of what preceded it (so `/foo/project_starburst/` wrongly matched even though
 * it isn't one of the three real routes). Pathnames are now matched against regexes DERIVED from
 * the real route patterns instead of a hand-rolled suffix check, so the two can't drift apart again.
 */

export const PROJECT_STARBURST_SEGMENT = 'project_starburst';

/** Worklist coding starburst route (maintenance-wrapped in `CMx.tsx`). */
export const WORKLIST_STARBURST = `/cmx/worklist/:serviceLine/case/:caseId/case-run/:id/${PROJECT_STARBURST_SEGMENT}/`;

/** Coding-quality starburst route (maintenance-wrapped in `CMx.tsx`). */
export const CODING_QUALITY_STARBURST = `/cmx/coding-quality/:serviceLine/case/:caseId/case-run/:id/${PROJECT_STARBURST_SEGMENT}/`;

/**
 * Grading (CQA) starburst route pattern, hardcoded as a literal: the base
 * (`CQA_QUALITY_ASSESSMENT_GRADING_SCREEN`, `/cmx/quality-assessment/workItem`) lives in
 * dashboard-ui core, which this isolated folder cannot import. `GRADING_STARBURST`
 * below is the builder `CMx.tsx` calls with its own copy of that base constant --
 * `routing.test.ts` asserts `GRADING_STARBURST_PATTERN === GRADING_STARBURST(theRealBase)` so the
 * hardcoded literal and the builder can't silently drift apart.
 */
export const GRADING_STARBURST_PATTERN = `/cmx/quality-assessment/workItem/:id/${PROJECT_STARBURST_SEGMENT}/`;

/** Grading (CQA) starburst route -- appends `/:id/project_starburst/` to the caller's base grading
 *  screen path constant (kept a function, not a literal, since the base path lives in `CMx.tsx`). */
export const GRADING_STARBURST = (base: string): string =>
  `${base}/:id/${PROJECT_STARBURST_SEGMENT}/`;

/**
 * Converts one `<Route exact path={pattern}>` pattern into a RegExp with equivalent matching
 * semantics: split on `/`, turn each `:param` segment into `[^/]+` (one non-slash segment -- the
 * same default `path-to-regexp` (react-router v5's matcher) uses), escape any regex metacharacters
 * in literal segments, re-join, anchor the whole string, and allow exactly one optional trailing
 * slash -- case-insensitively, matching RR5's default `sensitive: false`.
 */
function patternToRegex(pattern: string): RegExp {
  const noTrail = pattern.replace(/\/+$/, '');
  const body = noTrail
    .split('/')
    .map((seg) =>
      seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    .join('/');
  return new RegExp(`^${body}/?$`, 'i');
}

const STARBURST_ROUTE_REGEXES = [
  WORKLIST_STARBURST,
  CODING_QUALITY_STARBURST,
  GRADING_STARBURST_PATTERN
].map(patternToRegex);

export const isProjectStarburstPath = (pathname: string): boolean =>
  STARBURST_ROUTE_REGEXES.some((re) => re.test(pathname));
